import { TENURE_BAND_LABELS } from "#shared/reviewee-filters/tenureBuckets.ts";
import type { TenureBucketKey } from "#shared/reviewee-filters/tenureBuckets.ts";
import type { TrackOption } from "#shared/reviewee-filters/trackOptions.ts";
import { resolveLanguageKey } from "@packages/core/language/language.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button.tsx";
import { reviewCyclesI18NextInstanceKey } from "../../i18n/i18next.ts";
import { AddFilterButton } from "./AddFilterButton.tsx";
import { DepartmentFilterPanel } from "./DepartmentFilterPanel.tsx";
import { DirectManagersFilterPanel } from "./DirectManagersFilterPanel.tsx";
import { type FilterKind, FilterKinds } from "./filterKinds.ts";
import { FilterChipPopover } from "./FilterChipPopover.tsx";
import { GenderFilterPanel } from "./GenderFilterPanel.tsx";
import { JobTitleFilterPanel } from "./JobTitleFilterPanel.tsx";
import { ReportingLineFilterPanel } from "./ReportingLineFilterPanel.tsx";
import { RoleLevelFilterPanel } from "./RoleLevelFilterPanel.tsx";
import { SegmentFilterPanel } from "./SegmentFilterPanel.tsx";
import { TenureFilterPanel } from "./TenureFilterPanel.tsx";
import { TrackFilterPanel } from "./TrackFilterPanel.tsx";
import { useCachedManagerNames, useCachedSegmentNames } from "./useCachedOptionNames.ts";
import { useRevieweeFilters } from "./useRevieweeFilters.ts";
import { WorkLocationFilterPanel } from "./WorkLocationFilterPanel.tsx";

function toPersonChipValue(ids: string[], callerId: string, namesById: Map<string, string>, meLabel: string, countLabel: (count: number) => string) {
    if (ids.length === 1) {
        const id = ids[0];

        return id === callerId ? meLabel : (namesById.get(id) ?? countLabel(1));
    }

    return countLabel(ids.length);
}

function toChipValue(values: string[], countLabel: string) {
    return values.length === 1 ? (values[0] ?? countLabel) : countLabel;
}

interface FilterChipDescriptor {
    isSet: boolean;
    value: string;
    renderPanel: (close: () => void) => ReactNode;
}

export interface RevieweeFilterBarProps {
    leadingContent?: ReactNode;
    trailingContent?: ReactNode;
    onFilterApplied?: (kind: FilterKind) => void;
    onFiltersCleared?: () => void;
    onClearExtraFilters?: () => void;
    hasExtraActiveFilters?: boolean;
}

export function RevieweeFilterBar({ leadingContent, trailingContent, onFilterApplied, onFiltersCleared, onClearExtraFilters, hasExtraActiveFilters }: RevieweeFilterBarProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("FilterBar", { i18n: i18nextInstance });
    const { t: tKinds } = useTranslation("FilterKinds", { i18n: i18nextInstance });
    const { t: tOptions } = useTranslation("TrackOptions", { i18n: i18nextInstance });
    const language = resolveLanguageKey(i18nextInstance.language);

    const { managerIds, segmentIds, reportingLineIds, departments, genders, workLocations, jobLevels, jobTitles, tenure, track, clearFilters, removeFilter, hasActiveFilters, setFilters } = useRevieweeFilters();
    const callerId = useSession()?.memberId ?? "";

    const handleClearFilters = useCallback(() => {
        onFiltersCleared?.();
        onClearExtraFilters?.();
        clearFilters();
    }, [onFiltersCleared, onClearExtraFilters, clearFilters]);

    const applyFilter = useCallback(
        (kind: FilterKind, patch: Parameters<typeof setFilters>[0]) => {
            const isApplied = Object.values(patch ?? {}).some(value => (Array.isArray(value) ? value.length > 0 : Boolean(value)));

            if (isApplied) {
                onFilterApplied?.(kind);
            }

            return void setFilters(patch);
        },
        [onFilterApplied, setFilters]
    );

    const handleManagerSelectionChange = useCallback((ids: string[]) => applyFilter("directManagers", { managerIds: ids.length > 0 ? ids : null }), [applyFilter]);
    const handleReportingLineSelectionChange = useCallback((ids: string[]) => applyFilter("reportingLine", { reportingLineIds: ids.length > 0 ? ids : null }), [applyFilter]);
    const handleSegmentSelectionChange = useCallback((ids: string[]) => applyFilter("segment", { segmentIds: ids.length > 0 ? ids : null }), [applyFilter]);
    const handleDepartmentSelectionChange = useCallback((values: string[]) => applyFilter("department", { departments: values.length > 0 ? values : null }), [applyFilter]);
    const handleGenderSelectionChange = useCallback((values: string[]) => applyFilter("gender", { genders: values.length > 0 ? values : null }), [applyFilter]);
    const handleWorkLocationSelectionChange = useCallback((values: string[]) => applyFilter("workLocation", { workLocations: values.length > 0 ? values : null }), [applyFilter]);
    const handleJobLevelSelectionChange = useCallback((values: string[]) => applyFilter("roleLevel", { jobLevels: values.length > 0 ? values : null }), [applyFilter]);
    const handleJobTitleSelectionChange = useCallback((values: string[]) => applyFilter("jobTitle", { jobTitles: values.length > 0 ? values : null }), [applyFilter]);
    const handleTenureSelectionChange = useCallback((value: TenureBucketKey | null) => applyFilter("tenure", { tenure: value }), [applyFilter]);
    const handleTrackSelectionChange = useCallback((value: TrackOption | null) => applyFilter("track", { track: value }), [applyFilter]);

    const tenureBuckets = useMemo(() => (tenure ? [tenure] : []), [tenure]);
    const tracks = useMemo(() => (track ? [track] : []), [track]);

    const handleTenurePanelChange = useCallback((next: TenureBucketKey[]) => handleTenureSelectionChange(next[0] ?? null), [handleTenureSelectionChange]);
    const handleTrackPanelChange = useCallback((next: TrackOption[]) => handleTrackSelectionChange(next[0] ?? null), [handleTrackSelectionChange]);

    const managerNames = useCachedManagerNames();
    const segmentNames = useCachedSegmentNames();

    const toCountValue = useCallback((count: number) => t("valueCount", { count }), [t]);

    const chips: Record<FilterKind, FilterChipDescriptor> = {
        directManagers: {
            isSet: managerIds.length > 0,
            value: toPersonChipValue(managerIds, callerId, managerNames, t("me"), toCountValue),
            renderPanel: close => <DirectManagersFilterPanel selectedIds={managerIds} onSelectionChange={handleManagerSelectionChange} onBack={close} />
        },
        reportingLine: {
            isSet: reportingLineIds.length > 0,
            value: toPersonChipValue(reportingLineIds, callerId, managerNames, t("me"), toCountValue),
            renderPanel: close => <ReportingLineFilterPanel selectedIds={reportingLineIds} onSelectionChange={handleReportingLineSelectionChange} onBack={close} />
        },
        segment: {
            isSet: segmentIds.length > 0,
            value: (segmentIds.length === 1 ? segmentNames.get(segmentIds[0]) : undefined) || toCountValue(segmentIds.length),
            renderPanel: close => <SegmentFilterPanel selectedIds={segmentIds} onSelectionChange={handleSegmentSelectionChange} onBack={close} />
        },
        department: { isSet: departments.length > 0, value: toChipValue(departments, toCountValue(departments.length)), renderPanel: close => <DepartmentFilterPanel selectedValues={departments} onSelectionChange={handleDepartmentSelectionChange} onBack={close} /> },
        gender: { isSet: genders.length > 0, value: toChipValue(genders, toCountValue(genders.length)), renderPanel: close => <GenderFilterPanel selectedValues={genders} onSelectionChange={handleGenderSelectionChange} onBack={close} /> },
        workLocation: {
            isSet: workLocations.length > 0,
            value: toChipValue(workLocations, toCountValue(workLocations.length)),
            renderPanel: close => <WorkLocationFilterPanel selectedValues={workLocations} onSelectionChange={handleWorkLocationSelectionChange} onBack={close} />
        },
        roleLevel: { isSet: jobLevels.length > 0, value: toChipValue(jobLevels, toCountValue(jobLevels.length)), renderPanel: close => <RoleLevelFilterPanel selectedValues={jobLevels} onSelectionChange={handleJobLevelSelectionChange} onBack={close} /> },
        jobTitle: { isSet: jobTitles.length > 0, value: toChipValue(jobTitles, toCountValue(jobTitles.length)), renderPanel: close => <JobTitleFilterPanel selectedValues={jobTitles} onSelectionChange={handleJobTitleSelectionChange} onBack={close} /> },
        tenure: { isSet: Boolean(tenure), value: tenure ? (TENURE_BAND_LABELS[tenure]?.[language] ?? tenure) : "", renderPanel: close => <TenureFilterPanel selectedBuckets={tenureBuckets} onSelectionChange={handleTenurePanelChange} onBack={close} /> },
        track: { isSet: Boolean(track), value: track ? tOptions(track) : "", renderPanel: close => <TrackFilterPanel selectedTracks={tracks} onSelectionChange={handleTrackPanelChange} onBack={close} /> }
    };

    const showClear = hasActiveFilters || hasExtraActiveFilters;

    return (
        <div className="flex flex-wrap items-start gap-2">
            <div className="flex min-w-48 flex-1 flex-wrap items-center gap-2">
                {leadingContent}

                {FilterKinds.filter(kind => chips[kind].isSet).map(kind => (
                    <FilterChipPopover key={kind} tone="active" kind={kind} label={tKinds(kind)} value={chips[kind].value} ariaLabel={t("chipAriaLabel", { kind: tKinds(kind), value: chips[kind].value })} removeLabel={t("remove")} onRemove={removeFilter}>
                        {chips[kind].renderPanel}
                    </FilterChipPopover>
                ))}

                <AddFilterButton
                    selectedManagerIds={managerIds}
                    onManagerSelectionChange={handleManagerSelectionChange}
                    selectedReportingLineIds={reportingLineIds}
                    onReportingLineSelectionChange={handleReportingLineSelectionChange}
                    selectedSegmentIds={segmentIds}
                    onSegmentSelectionChange={handleSegmentSelectionChange}
                    selectedDepartments={departments}
                    onDepartmentSelectionChange={handleDepartmentSelectionChange}
                    selectedGenders={genders}
                    onGenderSelectionChange={handleGenderSelectionChange}
                    selectedWorkLocations={workLocations}
                    onWorkLocationSelectionChange={handleWorkLocationSelectionChange}
                    selectedJobLevels={jobLevels}
                    onJobLevelSelectionChange={handleJobLevelSelectionChange}
                    selectedJobTitles={jobTitles}
                    onJobTitleSelectionChange={handleJobTitleSelectionChange}
                    selectedTenure={tenure}
                    onTenureSelectionChange={handleTenureSelectionChange}
                    selectedTrack={track}
                    onTrackSelectionChange={handleTrackSelectionChange}
                />
            </div>

            {(showClear || trailingContent) && (
                <div className="flex flex-wrap items-center gap-1">
                    {showClear && (
                        <Button intent="plain" size="sm" onPress={handleClearFilters}>
                            {t("clear")}
                        </Button>
                    )}
                    {trailingContent}
                </div>
            )}
        </div>
    );
}
